<template>
    <div class="page-container">
        <div class="default-box">
            <h1>Account Security</h1>
            <div class="action-card">
                <h2>
                    Two-Factor Authentication
                    <span :class="twoFactorEnabled ? 'status-enabled' : 'status-disabled'" class="status-indicator">
                        {{ twoFactorEnabled ? 'Enabled' : 'Disabled' }}
                    </span>
                </h2>
                <div v-if="!twoFactorEnabled">
                    <p>
                        Add an extra layer of security to your account by enabling two-factor authentication (2FA).
                    </p>
                    <div class="center-content">
                        <button class="button-large" @click="enableTwoFactor">Enable Two-Factor
                            Authentication</button>
                    </div>
                    <div class="two-factor-setup center-content">
                        <p v-if="qrCodeUrl">Scan this QR code with your authenticator app:</p>
                        <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="QR Code for 2FA" />

                        <div v-if="recoverySeed" class="seed-container">
                            <h3>Your Recovery Seed</h3>
                            <p>
                                This is your recovery phrase. Keep it safe; it is required to recover your 2FA setup if
                                you lose access to your device.
                            </p>
                            <pre class="recovery-seed">{{ recoverySeed }}</pre>
                            <button @click="downloadRecoverySeed" class="button-large">Download Recovery Seed</button>
                        </div>

                        <!-- Show OTP fields only when QR code is available -->
                        <div v-if="qrCodeUrl && recoverySeed">
                            <p>Enter the code generated by your authenticator app:</p>
                            <input v-model="otp" type="text" placeholder="Enter OTP" />
                            <div class="button-group">
                                <button class="button-large" @click="verifyTwoFactor">Verify</button>
                                <button class="button-large-imp" @click="cancelTwoFactorSetup">Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="twoFactorEnabled && recoverySeed" class="seed-container">
                    <h3>Your Recovery Seed</h3>
                    <p>
                        This is your recovery phrase. Keep it safe; it is required to recover your 2FA setup if you lose
                        access to your device.
                    </p>
                    <pre class="recovery-seed">{{ recoverySeed }}</pre><br>
                    <div class="center-content"><button @click="downloadRecoverySeed" class="button-large">Download Recovery Seed</button></div>
                </div>
            </div>
        </div>
        <div class="center-content">
            <h1>Danger Zone</h1>
        </div>
        <div v-if="twoFactorEnabled" class="default-box-danger">
            <div class="delete-account-section">

                <h3>Remove Two Factor Authentication</h3>
                <p class="delete-warning">
                    Two-factor authentication (2FA) adds an important layer of security by requiring a second
                    verification step, protecting your account even if your login is compromised. Without 2FA,
                    you’re more vulnerable to hacking, phishing, and unauthorized access to sensitive information.
                    Keeping 2FA enabled is a quick, effective way to safeguard your accounts and enjoy peace of
                    mind.
                </p><br>
            </div>
            <div class="center-content">
                <button @click="disableTwoFactor" class="button-large-imp">
                    Disable Two-Factor Authentication
                </button>
            </div>
        </div>

        <div class="default-box-danger">
            <div class="delete-account-section">
                <h3>Deactivate Your Account</h3>
                <p class="delete-warning">
                    Deactivating your account will mark all associated data as inactive. In compliance with European 
                    GDPR and AML regulations, your data will be securely retained for a period of 90 days. During 
                    this time, your account can be reactivated if you choose to return. If no reactivation occurs 
                    within this period, all data will be permanently deleted. This action is final and cannot be 
                    undone after 90 days.
                </p><br>

                <!-- Initial Delete Button -->
                <button class="button-large-imp" v-if="!showFirstConfirmation && !showFinalConfirmation"
                    @click="showFirstConfirmation = true">
                    Deactivate My Account
                </button>

                <!-- First Confirmation -->
                <div v-if="showFirstConfirmation && !showFinalConfirmation">
                    <p class="confirmation-warning">
                        <br>Are you sure you want to deactivate your account?
                    </p>
                    <button class="confirm-button-acc" @click="showFinalConfirmation = true">
                        Yes, Proceed
                    </button>
                    <button @click="cancelDeletion">Cancel</button>
                </div>

                <!-- Final Confirmation -->
                <div v-if="showFinalConfirmation">
                    <p class="final-warning">
                        This is your final confirmation. Do you really want to deactivate your account?
                    </p>
                    <button class="confirm-button-acc"
                        @click="() => { console.log('Button Clicked'); finalizeDelete(); }">
                        Yes!
                    </button>
                    <button class="cancel-button-acc" @click="cancelDeletion">Cancel</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from "@/api";
import QRCode from "qrcode";

export default {
    data() {
        return {
            twoFactorEnabled: false,
            qrCodeUrl: null,
            recoverySeed: null,
            otp: "",
            showFinalConfirmation: false,
            showFirstConfirmation: false,
        };
    },
    created() {
        this.checkTwoFactorStatus();
    },
    methods: {
        // Check the status of Two-Factor Authentication
        async checkTwoFactorStatus() {
            try {
                const response = await api.get("/user/security/2fa-status");
                this.twoFactorEnabled = response.data.enabled;

                // Fetch the recovery seed if 2FA is enabled
                if (this.twoFactorEnabled) {
                    await this.fetchRecoverySeed();
                }
            } catch (error) {
                console.error("Failed to fetch 2FA status:", error);
            }
        },

        // Start Two-Factor Authentication setup
        async enableTwoFactor() {
            try {
                const response = await api.post("/user/security/enable-2fa");
                const { otpauthUrl, recoveryPhrase } = response.data;

                this.recoverySeed = recoveryPhrase; // Store the recovery phrase

                // Generate and display the QR code
                QRCode.toDataURL(otpauthUrl, (err, url) => {
                    if (err) {
                        console.error("Failed to generate QR code:", err);
                        return;
                    }
                    this.qrCodeUrl = url;
                });

                // console.log("Recovery seed captured:", this.recoverySeed); // DEBUG
            } catch (error) {
                console.error("Failed to enable 2FA:", error);
                alert("Unable to enable Two-Factor Authentication. Please try again.");
            }
        },


        // cancel 2fa setup and perform a rollback
        async cancelTwoFactorSetup() {
            try {
                // Clear temporary state in the frontend
                this.qrCodeUrl = null;
                this.recoverySeed = null;
                this.otp = ""; // Clear any input from the user

                // Notify the backend to delete the temporary 2FA data
                await api.post("/user/security/cancel-2fa-setup");

                alert("Two-Factor Authentication setup canceled.");
            } catch (error) {
                console.error("Failed to cancel 2FA setup:", error);
                alert("Failed to cancel 2FA setup. Please try again.");
            }
        },

        // Verify the OTP entered by the user and enable 2FA
        async verifyTwoFactor() {
            try {
                const response = await api.post("/user/security/verify-2fa", { otp: this.otp });
                if (response.data.message === "2FA verification successful.") {
                    alert("Two-Factor Authentication enabled successfully!");
                    this.twoFactorEnabled = true; // Update 2FA status
                    this.qrCodeUrl = null; // Clear the QR code display
                    this.recoverySeed = null; // Clear the recovery seed from display
                }
            } catch (error) {
                console.error("Failed to verify 2FA:", error);
                alert("Invalid OTP. Please try again.");
            }
        },

        // Generate and display a QR code from an otpauth URL
        generateQRCode(otpauthUrl) {
            QRCode.toDataURL(otpauthUrl, (err, url) => {
                if (err) {
                    console.error("Failed to generate QR code:", err);
                    return;
                }
                this.qrCodeUrl = url;
            });
        },

        // Fetch Recovery Seed
        async fetchRecoverySeed() {
            try {
                const response = await api.get("/user/security/get-2fa-seed");
                this.recoverySeed = response.data.recoverySeed;
                // console.log("Recovery seed fetched:", this.recoverySeed); // DEBUG
            } catch (error) {
                console.error("Failed to fetch recovery seed:", error.response?.data || error);
                alert("Unable to fetch recovery seed. Please try again.");
            }
        },

        // Download Recovery Seed as a .txt file
        downloadRecoverySeed() {
            if (!this.recoverySeed) {
                alert("No recovery seed available for download.");
                return;
            }

            const seedBlob = new Blob([this.recoverySeed], { type: "text/plain" });
            const downloadLink = document.createElement("a");
            downloadLink.href = URL.createObjectURL(seedBlob);
            downloadLink.download = "2fa-recovery-seed.txt";
            downloadLink.click();
        },

        // Disable 2FA
        async disableTwoFactor() {
            try {
                const response = await api.post("/user/security/disable-2fa");
                if (response.data.message === "Two-factor authentication has been disabled.") {
                    alert("Two-Factor Authentication has been disabled.");
                    this.twoFactorEnabled = false; // Update state
                    this.qrCodeUrl = null; // Clear any residual QR code
                }
            } catch (error) {
                console.error("Failed to disable 2FA:", error);
                alert("Failed to disable Two-Factor Authentication.");
            }
        },

        // delete accout
        cancelDeletion() {
            this.showFirstConfirmation = false;
            this.showFinalConfirmation = false;
        },
        async finalizeDelete() {
            try {
                const response = await api.delete("/user/profile/delete");
                if (response.status === 200) {
                    alert("Account deleted successfully. You will be logged out.");
                    localStorage.removeItem("token");
                    this.$router.push("/login");
                } else {
                    throw new Error("Failed to delete account. Please try again.");
                }
            } catch (error) {
                console.error("Error deleting account:", error.message);
                alert("An error occurred while deleting your account. Please try again.");
            }
        },
    },
};
</script>
